/**
 * Détection d'intentions dans le texte de recherche libre.
 *
 * Permet de transformer "trail < 25km ce week-end" en filtres structurés
 * (terrain=trail, kmMax=25, when=weekend) tout en conservant le texte
 * résiduel pour faire un match titre/ville classique.
 */

export type SearchIntentType =
  | 'distance'
  | 'terrain'
  | 'price'
  | 'date'
  | 'difficulty'
  | 'kmRange';

export interface SearchIntent {
  type: SearchIntentType;
  label: string;
  /** Clé technique du filtre, ex: "marathon", "trail", "weekend" */
  value: string;
}

export interface IntentFilters {
  distances: string[];
  terrains: string[];
  difficulties: string[];
  priceMax?: number;
  priceMin?: number;
  kmMax?: number;
  kmMin?: number;
  when?: 'weekend' | 'month';
}

export interface ParsedSearch {
  residualText: string;
  intents: SearchIntent[];
  filters: IntentFilters;
}

function normalize(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
}

/**
 * Chaque règle reçoit le texte normalisé et peut consommer (retirer) des
 * tokens. Les règles plus spécifiques (ex: "< 25 km") doivent passer avant
 * les plus génériques (ex: "25 km").
 */
type Rule = (input: string, ctx: ParseContext) => string;

interface ParseContext {
  intents: SearchIntent[];
  filters: IntentFilters;
}

function addIntent(ctx: ParseContext, intent: SearchIntent) {
  // Évite les doublons
  if (ctx.intents.some((i) => i.type === intent.type && i.value === intent.value)) return;
  ctx.intents.push(intent);
}

/** Strip a regex match from the input string */
function strip(input: string, match: RegExpExecArray): string {
  return (input.slice(0, match.index) + ' ' + input.slice(match.index + match[0].length)).replace(/\s+/g, ' ');
}

// ---- Rules --------------------------------------------------------------

/** "moins de 25 km", "< 25km", "25 km max", "jusqu'a 25km" */
const ruleKmMax: Rule = (input, ctx) => {
  const re = /(?:moins de|<|max(?:imum)?|jusqu'?a|sous)\s*(\d{1,3})\s*(?:k|km|kms|kilometres?)\b/;
  let m = re.exec(input);
  if (!m) {
    const re2 = /\b(\d{1,3})\s*(?:k|km|kms|kilometres?)\s*(?:max|maxi|maximum|ou moins)\b/;
    m = re2.exec(input);
  }
  if (m) {
    const km = parseInt(m[1], 10);
    if (km > 0 && km < 500) {
      ctx.filters.kmMax = km;
      addIntent(ctx, { type: 'kmRange', label: `< ${km} km`, value: `max:${km}` });
      return strip(input, m);
    }
  }
  return input;
};

/** "plus de 50 km", "> 50km", "50 km min" */
const ruleKmMin: Rule = (input, ctx) => {
  const re = /(?:plus de|>|min(?:imum)?|au moins)\s*(\d{1,3})\s*(?:k|km|kms|kilometres?)\b/;
  let m = re.exec(input);
  if (!m) {
    const re2 = /\b(\d{1,3})\s*(?:k|km|kms|kilometres?)\s*(?:min|mini|minimum|ou plus)\b/;
    m = re2.exec(input);
  }
  if (m) {
    const km = parseInt(m[1], 10);
    if (km > 0 && km < 500) {
      ctx.filters.kmMin = km;
      addIntent(ctx, { type: 'kmRange', label: `> ${km} km`, value: `min:${km}` });
      return strip(input, m);
    }
  }
  return input;
};

/** Distances nommées : marathon, semi, 10k, 5k, ultra, 100km, 42km... */
const distanceKeywords: Array<{ re: RegExp; bucket: string; label: string }> = [
  { re: /\bsemi[-\s]?marathon\b/, bucket: 'semi', label: 'Semi' },
  { re: /\bsemis?\b/, bucket: 'semi', label: 'Semi' },
  { re: /\bmarathons?\b/, bucket: 'marathon', label: 'Marathon' },
  { re: /\bultra[-\s]?(?:trail|marathon)?\b/, bucket: 'ultra', label: 'Ultra' },
  { re: /\b(?:10|dix)\s*(?:k|km|kms|kilometres?)\b/, bucket: '10k', label: '10K' },
  { re: /\b(?:5|cinq)\s*(?:k|km|kms|kilometres?)\b/, bucket: '5k', label: '5K' },
  { re: /\b21\s*(?:k|km|kms|kilometres?)\b/, bucket: 'semi', label: 'Semi' },
  { re: /\b42\s*(?:k|km|kms|kilometres?)\b/, bucket: 'marathon', label: 'Marathon' },
  { re: /\b(?:80|100|110|120|160|170|200)\s*(?:k|km|kms|kilometres?)\b/, bucket: 'ultra', label: 'Ultra' },
  { re: /\b10k\b/, bucket: '10k', label: '10K' },
  { re: /\b5k\b/, bucket: '5k', label: '5K' },
];

const ruleDistanceBucket: Rule = (input, ctx) => {
  let out = input;
  for (const { re, bucket, label } of distanceKeywords) {
    const m = re.exec(out);
    if (m) {
      if (!ctx.filters.distances.includes(bucket)) ctx.filters.distances.push(bucket);
      addIntent(ctx, { type: 'distance', label, value: bucket });
      out = strip(out, m);
    }
  }
  return out;
};

/** Terrain : trail, route, piste, mixte, montagne */
const terrainKeywords: Array<{ re: RegExp; terrain: string; label: string }> = [
  { re: /\btrails?\b/, terrain: 'trail', label: 'Trail' },
  { re: /\bmontagnes?\b/, terrain: 'trail', label: 'Montagne' },
  { re: /\bsentiers?\b/, terrain: 'trail', label: 'Trail' },
  { re: /\broutes?\b/, terrain: 'road', label: 'Route' },
  { re: /\bbitumes?\b/, terrain: 'road', label: 'Route' },
  { re: /\bpistes?\b/, terrain: 'track', label: 'Piste' },
  { re: /\bmixtes?\b/, terrain: 'mixed', label: 'Mixte' },
];

const ruleTerrain: Rule = (input, ctx) => {
  let out = input;
  for (const { re, terrain, label } of terrainKeywords) {
    const m = re.exec(out);
    if (m) {
      if (!ctx.filters.terrains.includes(terrain)) ctx.filters.terrains.push(terrain);
      addIntent(ctx, { type: 'terrain', label, value: terrain });
      out = strip(out, m);
    }
  }
  return out;
};

/** Difficulté : débutant, facile, intermédiaire, difficile, expert */
const difficultyKeywords: Array<{ re: RegExp; level: string; label: string }> = [
  { re: /\bdebutants?\b/, level: 'easy', label: 'Débutant' },
  { re: /\bfacile\b/, level: 'easy', label: 'Facile' },
  { re: /\bintermediaire\b/, level: 'medium', label: 'Intermédiaire' },
  { re: /\bmoyen\b/, level: 'medium', label: 'Intermédiaire' },
  { re: /\bdifficile\b/, level: 'hard', label: 'Difficile' },
  { re: /\bexperts?\b/, level: 'hard', label: 'Expert' },
  { re: /\bextreme\b/, level: 'extreme', label: 'Extrême' },
];

const ruleDifficulty: Rule = (input, ctx) => {
  let out = input;
  for (const { re, level, label } of difficultyKeywords) {
    const m = re.exec(out);
    if (m) {
      if (!ctx.filters.difficulties.includes(level)) ctx.filters.difficulties.push(level);
      addIntent(ctx, { type: 'difficulty', label, value: level });
      out = strip(out, m);
    }
  }
  return out;
};

/** Prix : "< 20€", "moins de 20 euros", "gratuit", "pas cher" */
const rulePrice: Rule = (input, ctx) => {
  let out = input;

  // Gratuit
  const mFree = /\b(gratuit|gratuites?|free|0\s*€|0\s*euros?)\b/.exec(out);
  if (mFree) {
    ctx.filters.priceMax = 0;
    addIntent(ctx, { type: 'price', label: 'Gratuit', value: 'free' });
    out = strip(out, mFree);
  }

  // < 20€, moins de 20 euros
  const mMax = /(?:moins de|<|max(?:imum)?|sous|jusqu'?a)\s*(\d{1,4})\s*(?:€|euros?\b|eur\b)/.exec(out);
  if (mMax) {
    const p = parseInt(mMax[1], 10);
    if (p >= 0 && p <= 1000) {
      ctx.filters.priceMax = p;
      addIntent(ctx, { type: 'price', label: `< ${p}€`, value: `max:${p}` });
      out = strip(out, mMax);
    }
  } else {
    const mMax2 = /\b(\d{1,4})\s*(?:€|euros?|eur)\s*(?:max|maxi|maximum|ou moins)\b/.exec(out);
    if (mMax2) {
      const p = parseInt(mMax2[1], 10);
      if (p >= 0 && p <= 1000) {
        ctx.filters.priceMax = p;
        addIntent(ctx, { type: 'price', label: `< ${p}€`, value: `max:${p}` });
        out = strip(out, mMax2);
      }
    }
  }

  // > 50€
  const mMin = /(?:plus de|>|min(?:imum)?|au moins)\s*(\d{1,4})\s*(?:€|euros?\b|eur\b)/.exec(out);
  if (mMin) {
    const p = parseInt(mMin[1], 10);
    if (p > 0 && p <= 1000) {
      ctx.filters.priceMin = p;
      addIntent(ctx, { type: 'price', label: `> ${p}€`, value: `min:${p}` });
      out = strip(out, mMin);
    }
  }

  // "pas cher" -> max 30€
  const mCheap = /\bpas cher\b/.exec(out);
  if (mCheap && ctx.filters.priceMax === undefined) {
    ctx.filters.priceMax = 30;
    addIntent(ctx, { type: 'price', label: 'Pas cher', value: 'cheap' });
    out = strip(out, mCheap);
  }

  return out;
};

/** Date : ce week-end, weekend, samedi, dimanche, ce mois */
const ruleDate: Rule = (input, ctx) => {
  let out = input;
  const mWk = /\b(?:ce\s+)?(?:week[-\s]?end|weekend|samedi|dimanche)\b/.exec(out);
  if (mWk) {
    ctx.filters.when = 'weekend';
    addIntent(ctx, { type: 'date', label: 'Ce week-end', value: 'weekend' });
    out = strip(out, mWk);
  }
  const mMonth = /\bce\s+mois(?:[-\s]?ci)?\b/.exec(out);
  if (mMonth) {
    ctx.filters.when = ctx.filters.when ?? 'month';
    addIntent(ctx, { type: 'date', label: 'Ce mois-ci', value: 'month' });
    out = strip(out, mMonth);
  }
  return out;
};

const RULES: Rule[] = [
  ruleKmMax,
  ruleKmMin,
  rulePrice,
  ruleDate,
  ruleDistanceBucket,
  ruleTerrain,
  ruleDifficulty,
];

export function parseSearchIntent(input: string): ParsedSearch {
  const ctx: ParseContext = {
    intents: [],
    filters: { distances: [], terrains: [], difficulties: [] },
  };

  let working = ' ' + normalize(input).replace(/[,;!?]/g, ' ').replace(/\s+/g, ' ') + ' ';

  for (const rule of RULES) {
    working = rule(working, ctx);
  }

  // Nettoyage des marqueurs orphelins (<, >, €, km isolés)
  working = working
    .replace(/\b(?:moins de|plus de|max|maxi|maximum|min|mini|minimum|jusqu'?a|sous|au moins|ou moins|ou plus)\b/g, ' ')
    .replace(/[<>€]/g, ' ')
    .replace(/\b(?:k|km|kms|kilometres?|euros?|eur)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return {
    residualText: working,
    intents: ctx.intents,
    filters: ctx.filters,
  };
}

/**
 * Construit l'URL `/explorer?...` à partir d'une recherche parsée.
 */
export function buildExplorerUrl(parsed: ParsedSearch): string {
  const params = new URLSearchParams();
  if (parsed.residualText) params.set('q', parsed.residualText);
  if (parsed.filters.distances.length) params.set('distances', parsed.filters.distances.join(','));
  if (parsed.filters.terrains.length) params.set('terrains', parsed.filters.terrains.join(','));
  if (parsed.filters.difficulties.length) params.set('difficulties', parsed.filters.difficulties.join(','));
  if (parsed.filters.priceMax !== undefined) params.set('max_price', String(parsed.filters.priceMax));
  if (parsed.filters.priceMin !== undefined) params.set('min_price', String(parsed.filters.priceMin));
  if (parsed.filters.kmMax !== undefined) params.set('max_km', String(parsed.filters.kmMax));
  if (parsed.filters.kmMin !== undefined) params.set('min_km', String(parsed.filters.kmMin));
  if (parsed.filters.when) params.set('when', parsed.filters.when);
  const qs = params.toString();
  return qs ? `/explorer?${qs}` : '/explorer';
}

// ---- Helpers d'application aux events ----------------------------------

function getAllDistancesKm(event: any): number[] {
  const races = event.event_races || event.races || [];
  const raceDistances = races.map((r: any) => r.distance_km).filter(Boolean) as number[];
  return event.distance_km ? [event.distance_km, ...raceDistances] : raceDistances;
}

function matchesDistanceBucket(km: number, bucket: string): boolean {
  switch (bucket) {
    case '5k': return km >= 4 && km <= 6;
    case '10k': return km >= 9 && km <= 12;
    case 'semi': return km >= 20 && km <= 22;
    case 'marathon': return km >= 40 && km <= 44;
    case 'ultra': return km > 44;
    default: return true;
  }
}

function getMinPriceEuros(event: any): number {
  if (event.display_price_cents !== undefined && event.display_price_cents !== null) {
    return event.display_price_cents / 100;
  }
  return (event.price_cents || 0) / 100;
}

function isInWeekend(dateStr: string): boolean {
  const d = new Date(dateStr);
  const now = new Date();
  const day = d.getDay(); // 0=dim, 6=sam
  if (day !== 0 && day !== 6) return false;
  const diff = d.getTime() - now.getTime();
  return diff >= 0 && diff <= 9 * 86_400_000; // dans les 9 prochains jours
}

function isInCurrentMonth(dateStr: string): boolean {
  const d = new Date(dateStr);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
}

/**
 * Applique les filtres d'intention à un événement.
 */
export function eventMatchesIntents(event: any, filters: IntentFilters): boolean {
  // Distance buckets
  if (filters.distances.length) {
    const ds = getAllDistancesKm(event);
    if (!ds.length) return false;
    if (!filters.distances.some((b) => ds.some((km) => matchesDistanceBucket(km, b)))) {
      return false;
    }
  }

  // km range (sur n'importe quelle distance de l'event)
  if (filters.kmMax !== undefined || filters.kmMin !== undefined) {
    const ds = getAllDistancesKm(event);
    if (!ds.length) return false;
    const min = filters.kmMin ?? 0;
    const max = filters.kmMax ?? Infinity;
    if (!ds.some((km) => km >= min && km <= max)) return false;
  }

  // Terrain
  if (filters.terrains.length) {
    if (!event.terrain || !filters.terrains.includes(event.terrain)) return false;
  }

  // Difficulté
  if (filters.difficulties.length) {
    if (!event.difficulty || !filters.difficulties.includes(event.difficulty)) return false;
  }

  // Prix
  if (filters.priceMax !== undefined || filters.priceMin !== undefined) {
    const p = getMinPriceEuros(event);
    if (filters.priceMax !== undefined && p > filters.priceMax) return false;
    if (filters.priceMin !== undefined && p < filters.priceMin) return false;
  }

  // Date
  if (filters.when === 'weekend' && !isInWeekend(event.event_date)) return false;
  if (filters.when === 'month' && !isInCurrentMonth(event.event_date)) return false;

  return true;
}

export function hasAnyIntent(filters: IntentFilters): boolean {
  return (
    filters.distances.length > 0 ||
    filters.terrains.length > 0 ||
    filters.difficulties.length > 0 ||
    filters.priceMax !== undefined ||
    filters.priceMin !== undefined ||
    filters.kmMax !== undefined ||
    filters.kmMin !== undefined ||
    filters.when !== undefined
  );
}